/**
 * FmNavActiveContext — Active navigation state from catalog (7usro.3)
 * 
 * Provides resolved active primaryNavId and groupId based on current pathname,
 * WITHOUT requiring pathname string matching in every component.
 * 
 * Consumed by FmTopNav, FmMegaMenu, and FmMobileNavDrawer.
 * 
 * GOVERNING: Active state comes from catalog ownership, not pathname hack.
 */

import React, { useMemo } from 'react';
import type { FmRouteCatalogData } from './FmRouteCatalog';
import { getCatalogRouteFromPath } from './FmRouteCatalog';
import type { FmNavActiveContextValue } from './FmNavActiveContext.types';
import { NavActiveContext } from './FmNavActiveContext.types';

interface FmNavActiveProviderProps {
  children: React.ReactNode;
  catalog: FmRouteCatalogData;
  pathname: string;
}

/**
 * Provider: resolves active navigation state from pathname + catalog.
 * Place this high in the app shell so nav components can use it.
 */
export const FmNavActiveProvider: React.FC<FmNavActiveProviderProps> = ({
  catalog,
  pathname,
  children,
}) => {
  const state = useMemo(() => {
    const route = getCatalogRouteFromPath(catalog, pathname);

    return {
      activePrimaryNavId: route?.primaryNavId,
      activeRouteId: route?.activeMenuRouteId ?? route?.id,
      activeGroupId: route?.groupId,
    };
  }, [catalog, pathname]);

  const value: FmNavActiveContextValue = {
    state,
    catalog,
  };

  return (
    <NavActiveContext.Provider value={value}>
      {children}
    </NavActiveContext.Provider>
  );
};
